import type { JSX } from "react";
import { useStatsFetch } from "../../api/hooks/useStatsFetch";

/**
 * cross-layer-consistency-check (Phase ε 第 5 spec) Task 12:
 * ScenarioLineagePanel の「整合性アラート」 section (= AI suggest only doctrine の minimal 表示)。
 * pending alert のみを 件数 + 概要で list、 creator decision の編集 UI は base scope では
 * 提供しない (= future creator-tools UI 別 spec で配線)。
 *
 * - props: `{ scenarioId }`
 * - useStatsFetch<ConsistencyAlertListResponse> 経由で `?status=pending` filter
 * - 0 件で「整合性アラートなし」 表示
 * - 1+ 件で件数 + 各 alert の layer pair + description で list (= max 5 件 preview)
 * - data-testid="scenario-lineage-consistency-alert-section"
 */

function resolveBaseUrl(): string {
  return (
    (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api/v1"
  );
}

interface ConsistencyAlertSummary {
  id: string;
  layerSource: "A" | "B" | "C";
  layerTarget: "A" | "B" | "C";
  detail: { description?: string; suggestedAction?: string; confidence?: number };
}

interface ConsistencyAlertListResponse {
  alerts: ConsistencyAlertSummary[];
}

const LAYER_LABEL: Record<"A" | "B" | "C", string> = {
  A: "canonical",
  B: "scenario",
  C: "worldline",
};

export interface ConsistencyAlertSectionProps {
  scenarioId: string;
}

export function ConsistencyAlertSection({
  scenarioId,
}: ConsistencyAlertSectionProps): JSX.Element {
  const state = useStatsFetch<ConsistencyAlertListResponse>(
    `${resolveBaseUrl()}/scenarios/${encodeURIComponent(scenarioId)}/consistency-alerts?status=pending`,
    "整合性 alert",
  );

  return (
    <section
      data-testid="scenario-lineage-consistency-alert-section"
      className="flex flex-col gap-1 px-2 py-2 bg-slate-900/40 rounded"
    >
      <h3 className="text-xs text-slate-300/80 font-medium">整合性アラート</h3>
      {state.isLoading ? (
        <p
          data-testid="scenario-lineage-consistency-alert-loading"
          className="text-xs text-slate-200/60"
        >
          読み込み中…
        </p>
      ) : state.error !== null || state.data === null ? null : state.data.alerts
          .length === 0 ? (
        <p
          data-testid="scenario-lineage-consistency-alert-empty"
          className="text-xs text-slate-200/60"
        >
          整合性アラートなし
        </p>
      ) : (
        <ul
          data-testid="scenario-lineage-consistency-alert-list"
          className="text-xs flex flex-col gap-1 text-slate-100"
        >
          <li className="text-slate-300/70">
            未処理 alert: {state.data.alerts.length} 件
          </li>
          {state.data.alerts.slice(0, 5).map((alert) => (
            <li key={alert.id} className="flex flex-col gap-0.5">
              <span className="text-slate-300/70">
                {LAYER_LABEL[alert.layerSource]} ↔ {LAYER_LABEL[alert.layerTarget]}
              </span>
              {alert.detail.description ? (
                <span className="text-slate-100">{alert.detail.description}</span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
